import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { collateralApi, ApiError } from '../services/collateralApi';
import type { PropertyListItem } from '../types/Property';

export default function usePropertyPageList() {
    const navigate = useNavigate();
    const [refreshKey, setRefreshKey] = useState(0);
    const [showNewForm, setShowNewForm] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [sortField, setSortField] = useState('updatedAt');
    const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');
    const [propertyType, setPropertyType] = useState('');
    const [archived, setArchived] = useState('false');
    const [rows, setRows] = useState<PropertyListItem[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const pageSize = 100;
    const [isLoading, setIsLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [filterCounts, setFilterCounts] = useState<Record<string, any>>({});
    const [apiInfo, setApiInfo] = useState({ version: '', environment: '' });

    // Side panel
    const [selectedProperty, setSelectedProperty] = useState<any>(null);
    const [propertyDetail, setPropertyDetail] = useState<any>(null);
    const [isLoadingDetail, setIsLoadingDetail] = useState(false);

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const result = await collateralApi.getProperties({
                page,
                pageSize,
                search: search || undefined,
                propertyType: propertyType || undefined,
                archived: archived || undefined,
                sortField: sortField || undefined,
                sortOrder: sortOrder || undefined,
            });
            setRows(result.items || []);
            setTotal(result.total || 0);
            setFilterCounts(result.filterCounts || {});
            setApiInfo({ version: result.apiVersion || '', environment: result.environment || '' });
        } catch (e: any) {
            if (e instanceof ApiError) {
                if (e.status === 403) return navigate('/denied');
                if (e.status === 404) return navigate('/not-found');
            }
            navigate('/error');
        } finally {
            setIsLoading(false);
            setInitialLoading(false);
        }
    }, [page, search, propertyType, archived, sortField, sortOrder, navigate]);

    useEffect(() => { fetchData(); }, [fetchData, refreshKey]);

    const refresh = () => setRefreshKey(k => k + 1);

    const handleRowClick = async (row: PropertyListItem) => {
        setSelectedProperty(row);
        setIsLoadingDetail(true);
        try {
            const detail = await collateralApi.getPropertyView(row.id);
            setPropertyDetail(detail);
        } catch {
            setPropertyDetail(null);
        } finally {
            setIsLoadingDetail(false);
        }
    };

    const closePanel = () => {
        setSelectedProperty(null);
        setPropertyDetail(null);
    };

    return {
        rows, total, page, setPage, pageSize,
        isLoading, initialLoading, filterCounts,
        showNewForm, setShowNewForm,
        searchInput, setSearchInput, search, setSearch,
        sortField, setSortField, sortOrder, setSortOrder,
        propertyType, setPropertyType,
        archived, setArchived,
        refresh, handleRowClick,
        selectedProperty, propertyDetail, isLoadingDetail, closePanel,
        apiInfo,
    };
}
